import { useState } from 'react'
import { Icon } from '../Icon'
import { useCreateCategory, useUpdateCategory, useDeleteCategory } from '../../hooks/useProgramme'
import type { ProgrammeCategory } from '../../types'

// Admin CRUD over the Programming Overview category palette (spec 41 §5.3).
// Delete is guarded server-side (409 when the category is still used by a
// block) — surfaced here as an inline message.

export function ManageCategoriesModal({
  categories, onClose
}: {
  categories: ProgrammeCategory[]
  onClose: () => void
}) {
  const createCategory = useCreateCategory()
  const updateCategory = useUpdateCategory()
  const deleteCategory = useDeleteCategory()

  const [drafts, setDrafts] = useState<Record<number, { label: string; colour: string }>>(
    () => Object.fromEntries(categories.map((c) => [c.id, { label: c.label, colour: c.colour }]))
  )
  const [newLabel, setNewLabel] = useState('')
  const [newColour, setNewColour] = useState('#3D5A73')
  const [err, setErr] = useState<string | null>(null)

  function setDraft(id: number, patch: Partial<{ label: string; colour: string }>) {
    setDrafts((d) => ({ ...d, [id]: { ...d[id]!, ...patch } }))
  }

  async function saveRow(c: ProgrammeCategory) {
    const draft = drafts[c.id]
    if (!draft) return
    setErr(null)
    try {
      await updateCategory.mutateAsync({ id: c.id, body: { label: draft.label.trim(), colour: draft.colour } })
    } catch (e) {
      setErr((e as Error).message)
    }
  }

  async function removeRow(c: ProgrammeCategory) {
    setErr(null)
    try {
      await deleteCategory.mutateAsync(c.id)
    } catch (e) {
      const msg = (e as Error).message
      setErr(msg.includes('409') ? `“${c.label}” is still used by blocks — reassign or delete those first.` : msg)
    }
  }

  async function addNew() {
    if (!newLabel.trim()) { setErr('New category needs a label.'); return }
    setErr(null)
    try {
      const created = await createCategory.mutateAsync({ label: newLabel.trim(), colour: newColour })
      setDrafts((d) => ({ ...d, [created.id]: { label: created.label, colour: created.colour } }))
      setNewLabel('')
    } catch (e) {
      setErr((e as Error).message)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mt-16 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg">Manage categories</div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink" aria-label="Close"><Icon name="x" size={18} /></button>
        </div>

        <div className="space-y-2">
          {categories.map((c) => {
            const draft = drafts[c.id] ?? { label: c.label, colour: c.colour }
            return (
              <div key={c.id} className="flex items-center gap-2">
                <input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(draft.colour) ? draft.colour : '#888888'}
                  onChange={(e) => setDraft(c.id, { colour: e.target.value })}
                  className="w-9 h-9 rounded border border-line cursor-pointer flex-shrink-0"
                  aria-label={`${c.label} colour`}
                />
                <input
                  value={draft.label}
                  onChange={(e) => setDraft(c.id, { label: e.target.value })}
                  className="flex-1 border border-line rounded px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-green"
                />
                <button
                  type="button"
                  onClick={() => saveRow(c)}
                  className="px-2.5 py-1.5 rounded text-xs font-semibold border border-line text-green hover:border-green"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => removeRow(c)}
                  className="p-1.5 text-ink-soft/70 hover:text-oxblood"
                  aria-label={`Delete ${c.label}`}
                >
                  <Icon name="x" size={15} />
                </button>
              </div>
            )
          })}
        </div>

        <div className="mt-4 pt-4 border-t border-line">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-soft mb-2">Add a category</div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={newColour}
              onChange={(e) => setNewColour(e.target.value)}
              className="w-9 h-9 rounded border border-line cursor-pointer flex-shrink-0"
              aria-label="New category colour"
            />
            <input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Private hire"
              className="flex-1 border border-line rounded px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-green"
            />
            <button
              type="button"
              onClick={addNew}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold bg-green text-white hover:opacity-90"
            >
              <Icon name="plus" size={14} />Add
            </button>
          </div>
        </div>

        {err && <div className="mt-3 text-sm text-oxblood">{err}</div>}
      </div>
    </div>
  )
}
